import { durationWords } from "../../../operator/src/duration.js";

// Turning what xp/operator computed into what a cell says.
//
// NOTHING IS CALCULATED HERE AND THAT IS THE WHOLE DISCIPLINE OF THIS FILE.
// xp/operator is pure logic with no HTTP and no rendering, deliberately, and it
// already decides every judgement on this screen: what counts as silent, which
// queue depth is worth mentioning, what sentence a crew member reads, and in what
// order the attention list sorts. Recomputing any of it here would put a second
// definition of "silent" in the estate, in a language with a different idea of
// what a date is, and the two would diverge on the day one of them was tuned.
// So this file formats and nothing else.
//
// THE ONE RULE IT DOES ENFORCE, BECAUSE IT IS A RENDERING RULE RATHER THAN A
// CALCULATION. xp/operator's first rule is that nothing unknown is shown as a
// zero: "a device that has never reported its queue is not a device with an
// empty queue, and rendering one as the other is how a tablet gets packed with a
// fortnight of registrations on it." Every nullable field it produces arrives
// here as null, and the way that rule gets broken in a browser is one character
// long — `{value ?? 0}` instead of `{value}` — written by somebody removing a
// React warning about rendering null.
//
// UNDEFINED IS TREATED AS UNKNOWN AS WELL AS NULL, and that is not defensive
// programming for its own sake. The value arrives as JSON off a network. A field
// the server stopped sending arrives as `undefined` rather than `null`, and a
// presenter that only guarded against null would render it as an empty cell,
// which on this screen reads as zero to the person looking at it.

/** What a cell says when there is genuinely nothing to say. One string, so the check can find it. */
export const UNKNOWN = "not reported";

/**
 * A count, or the word.
 *
 * Zero is a count and is printed as one. The distinction this preserves is the
 * one xp/operator cares about — between a device holding nothing and a device
 * that has not said — and collapsing them in either direction loses it.
 */
export function count(value: number | null | undefined): string {
  return typeof value === "number" && Number.isFinite(value) ? String(value) : UNKNOWN;
}

/** A percentage, or the word. Suffixed here so no caller appends the sign to `UNKNOWN`. */
export function percent(value: number | null | undefined): string {
  return typeof value === "number" && Number.isFinite(value) ? `${value}%` : UNKNOWN;
}

/** What a cell says where the server withheld a rate on purpose. Its own string, so the check can find it. */
export const TOO_FEW = "too few to say";

/**
 * A share between nought and one, as a percentage — or the words for a rate the
 * server declined to give.
 *
 * A SEPARATE FUNCTION FROM `percent` BECAUSE THE INPUTS ARE DIFFERENT ANIMALS.
 * `percent` formats a battery reading, which arrives as 74 and means 74%.
 * `optIn.ts` sends `granted / asked`, which arrives as 0.74 and means the same
 * thing; running it through `percent` would put "0.74%" on a screen, and a
 * consent rate of nearly nought is a plausible enough number that nobody would
 * query it.
 *
 * NULL IS NOT AN ABSENCE HERE, IT IS A REFUSAL, and it wants its own word rather
 * than `UNKNOWN`. optIn.ts withholds the percentage below twenty people asked,
 * on the argument that "100% over three registrations invites exactly the wrong
 * conclusion twice"; the counts are still on the row and are still facts. "Not
 * reported" would describe that as a gap in what the server knows, which is the
 * opposite of what happened — it knows, and it is declining to reduce two
 * honest numbers to a misleading one.
 *
 * The rounding is `Math.round(rate * 100)`, character for character optIn.ts's
 * own, and that is a deliberate duplication of one arithmetic operation rather
 * than an oversight: this figure sits in a table beside that file's sentence,
 * and a cell reading 74% next to a sentence reading "73% of 190 agreed" is a
 * client ringing up about which one is true. check.mjs asserts the two agree by
 * reading the cell and the sentence out of the rendered screen, which is the
 * only place the disagreement would ever show up.
 */
export function share(value: number | null | undefined): string {
  if (value === null) return TOO_FEW;
  if (typeof value !== "number" || !Number.isFinite(value)) return UNKNOWN;
  return `${Math.round(value * 100)}%`;
}

/**
 * Bytes as something readable, or the word.
 *
 * Binary units, because it is free disk on a tablet and that is what the device
 * reported. Rounded to one decimal, because the difference between 1.2 GB and
 * 1.23 GB has never changed anybody's decision and the second is harder to read
 * across a table.
 */
export function bytes(value: number | null | undefined): string {
  if (typeof value !== "number" || !Number.isFinite(value)) return UNKNOWN;

  const units = ["B", "KiB", "MiB", "GiB", "TiB"];
  let scaled = value;
  let unit = 0;
  while (scaled >= 1024 && unit < units.length - 1) {
    scaled /= 1024;
    unit += 1;
  }
  return `${unit === 0 ? scaled : Math.round(scaled * 10) / 10} ${units[unit]}`;
}

/**
 * A timestamp in the reader's own time zone, or the word.
 *
 * THE READER'S ZONE AND NOT THE VENUE'S, WHICH IS A KNOWN LIMITATION RATHER THAN
 * A CHOICE I AM DEFENDING. xp/kiosk has a whole contract module about reading a
 * schedule in the venue's own time, on the grounds that noon at the stand is not
 * noon on the machine. The same argument applies to a client in London reading a
 * report from a show in Dubai, and the console cannot fix it today because
 * nothing in the operator view carries the activation's time zone — there is no
 * field to read it from. Local time is at least a zone the reader can name.
 * Recorded in xp/CONSOLE-REPORT.md, because the shape of the fix is a field on
 * the report rather than anything in this file.
 *
 * An unparseable string comes back as the raw value rather than as `UNKNOWN`. The
 * server did say something, and showing what it said is more useful to whoever
 * has to work out why than replacing it with the word for "it said nothing".
 */
export function moment(value: string | null | undefined): string {
  if (typeof value !== "string" || value === "") return UNKNOWN;
  const parsed = Date.parse(value);
  if (Number.isNaN(parsed)) return value;
  return new Date(parsed).toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** Minutes as a phrase, or the word. */
export function minutes(value: number | null | undefined): string {
  if (typeof value !== "number" || !Number.isFinite(value)) return UNKNOWN;
  if (value < 1) return "under a minute ago";
  if (value < 60) return `${value} min ago`;
  return `${durationWords(value)} ago`;
}

/**
 * The class for a device status, refusing to invent one.
 *
 * The design's: a status is a word in the mono face, and the quiet tint for a
 * tablet nothing is known about, `unknown` (TASKS §143, step 5). The words
 * carry the reading, so the colours this used to add on top of them (green,
 * amber, red) are gone; a tablet gone silent is said in its word, its row's
 * accent and the attention list. A status this build has never heard of is
 * quiet too rather than plain, because a device whose status came back as a
 * word we do not know is a server and a console on different builds — which is
 * worth looking uncertain rather than looking fine.
 */
export function statusClass(status: string): string {
  switch (status) {
    case "healthy":
    case "quiet":
    case "silent":
      return "status";
    default:
      return "status status-quiet";
  }
}
